import React, { useRef } from 'react'
import { View } from 'react-native'
import { useDispatch, useSelector } from 'react-redux'
import moment from 'moment'
import { useForm } from 'react-hook-form'
import { upperFirst } from 'lodash'
import { useRoute } from '@react-navigation/native'
import { StyleService, useStyleSheet } from '@src/style/service'
import { NotificationEngineReminderDayOfWeek } from '@src/types'
import { Text, IconName } from '@src/components/base'
import { BottomSheet, CircleIcon } from '@src/components'
import { useSnack } from '@src/utils/navigatorContext'
import { useGoBack } from '@src/utils'
import { AppRouteProp } from '@src/navigation/types'
import { remindersHoursIntervalSelector } from '@src/selectors/app'
import { ReminderFormData, ReminderFormInputs } from './ReminderFormInputs'
import {
  buildDayOfWeek,
  buildHoursInterval,
  buildTimeOfDay,
  frequencyKeyByTitle,
  frequencyTitle,
  orderIntervalRanges,
} from './utils'

export const EditReminder = () => {
  const styles = useStyleSheet(themedStyles)
  const showSnack = useSnack()
  const dispatch = useDispatch()
  const {
    params: { reminder, reminderConfig: reminderKind },
  } = useRoute<AppRouteProp<'EditReminderModal'>>()
  const goBack = useGoBack()
  const isSubmitting = useRef(false)

  const reminderIntervalSettings = useSelector(remindersHoursIntervalSelector)
  const hoursIntervalRanges = orderIntervalRanges(reminderIntervalSettings.glucoseCheckOptions)
  const currentHoursInterval = hoursIntervalRanges.find(
    (option) => option.value === reminder.hoursInterval,
  )?.text

  const { control, handleSubmit } = useForm<ReminderFormData>({
    defaultValues: {
      kind: reminder.kind,
      frequency: frequencyTitle(reminder.frequency),
      timeOfDay: reminder.timeOfDay ?? moment().format('hh:mmA'),
      dayOfWeek: upperFirst(reminder.dayOfWeek ?? NotificationEngineReminderDayOfWeek.Monday),
      hoursInterval: currentHoursInterval,
    },
  })

  const onDeleteButtonPress = () => {
    if (isSubmitting.current) {
      return
    }
    isSubmitting.current = true
    dispatch({
      type: 'settings/deleteReminder',
      payload: { id: reminder.id },
      success: () => goBack(),
      failure: (error: any) => {
        if (error?.message) {
          showSnack(error.message, null, 'error')
        }
      },
      complete: () => {
        isSubmitting.current = false
      },
    })
  }

  const onSaveButtonPress = handleSubmit((data: ReminderFormData) => {
    if (isSubmitting.current) {
      return
    }
    isSubmitting.current = true

    const payload = {
      id: reminder.id,
      kind: reminderKind.key,
      frequency: frequencyKeyByTitle(data.frequency),
      dayOfWeek: buildDayOfWeek(data),
      timeOfDay: buildTimeOfDay(data),
      hoursInterval: buildHoursInterval(data, hoursIntervalRanges),
      __typename: 'Reminder',
    }
    dispatch({
      type: 'settings/updateReminder',
      payload,
      success: () => goBack(),
      failure: (error: any) => {
        if (error?.message) {
          showSnack(error.message, null, 'error')
        }
      },
      complete: () => {
        isSubmitting.current = false
      },
    })
  })

  return (
    <BottomSheet
      header="Edit Reminder"
      showDismissButton
      dismissButtonPosition="top"
      dismissButtonText="Cancel"
      primaryButton={{
        onPress: onSaveButtonPress,
        text: 'Save',
      }}
      secondaryButton={{
        onPress: onDeleteButtonPress,
        text: 'Delete Reminder',
        type: 'transparent',
        textStyle: styles.deleteButtonText,
      }}
      buttonDirection="column-reverse"
    >
      <View style={styles.typeTitle}>
        {!!reminderKind.icon && (
          <CircleIcon name={reminderKind.icon as IconName} style={styles.icon} />
        )}
        <Text type="title-2">{reminderKind.title}</Text>
      </View>
      <ReminderFormInputs
        reminderKind={reminderKind}
        control={control}
        hoursIntervalRanges={hoursIntervalRanges}
      />
    </BottomSheet>
  )
}
const themedStyles = StyleService.create({
  typeTitle: {
    marginBottom: 16,
    flexDirection: 'row',
    alignItems: 'center',
  },
  deleteButtonText: {
    color: 'theme.error.base',
  },
  icon: {
    marginRight: 16,
    padding: 16,
  },
})
